
import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Business } from "@/types";
import ViewToggle from "./listings/ViewToggle";
import BusinessGrid from "./listings/BusinessGrid";
import BusinessTable from "./listings/BusinessTable";
import BusinessesPagination from "./listings/BusinessesPagination";
import LoadingState from "./listings/LoadingState";
import EmptyState from "./listings/EmptyState";
import ErrorState from "./listings/ErrorState";
import { useIsMobile } from "@/hooks/use-mobile";

interface BusinessListingsProps {
  isLoading: boolean;
  error: Error | null;
  viewMode: "grid" | "list";
  setViewMode: (mode: "grid" | "list") => void;
  filteredBusinesses: Business[];
  paginatedBusinesses: Business[];
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  setActiveCategory: (category: undefined) => void;
  setCityFilter: (city: undefined) => void;
  setRegionFilter: (region: undefined) => void;
}

const BusinessListings: React.FC<BusinessListingsProps> = ({
  isLoading,
  error,
  viewMode,
  setViewMode,
  filteredBusinesses,
  paginatedBusinesses,
  totalPages,
  currentPage,
  setCurrentPage,
  searchTerm,
  setSearchTerm,
  setActiveCategory,
  setCityFilter,
  setRegionFilter,
}) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="md:col-span-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
        <div>
          {isLoading ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <p className="text-sm md:text-base text-muted-foreground">
              {filteredBusinesses.length} {filteredBusinesses.length === 1 ? 'result' : 'results'} found
            </p>
          )}
        </div>
        <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
      </div>
      
      {isLoading ? (
        <LoadingState viewMode={viewMode} />
      ) : error ? (
        <ErrorState />
      ) : filteredBusinesses.length === 0 ? (
        <EmptyState 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setActiveCategory={setActiveCategory}
          setCityFilter={setCityFilter}
          setRegionFilter={setRegionFilter}
        />
      ) : viewMode === "grid" ? (
        <BusinessGrid businesses={paginatedBusinesses} />
      ) : (
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <BusinessTable businesses={paginatedBusinesses} />
        </div>
      )}
      
      <BusinessesPagination 
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default BusinessListings;
